import { BitmapLayer } from "@deck.gl/layers";
import type { ShaderModule } from "@luma.gl/shadertools";

type Props    = { weight: number; texSize: [number, number] };
type Uniforms = { weight: number; texSize: [number, number] };

const uniformBlock = `\
uniform lanczosUniforms {
    float weight;
    vec2  texSize;
} lanczos;
`;

const samplerForwardDecl = `
uniform sampler2D bitmapTexture;
`;

const lanczosModule = {
    name: "lanczos",
    uniformTypes: { weight: "f32", texSize: "vec2<f32>" },
    defaultUniforms: { weight: 3, texSize: [1, 1] },
    fs: uniformBlock + samplerForwardDecl,
    inject: {
        "fs:DECKGL_FILTER_COLOR": `
            float a     = lanczos.weight;
            int   START = -int( a ) + 1;
            int   END   =  int( a );
            int   SIZE  = END - START + 1;

            vec2  pixel  = geometry.uv * lanczos.texSize;
            vec2  center = floor( pixel - 0.5 ) + 0.5;
            ivec2 imax   = ivec2( lanczos.texSize ) - 1;

            const float PI = 3.14159265;

            // precompute 1D weights (max SIZE = 6 for a=3)
            float wx[6], wy[6];
            for ( int i = START; i <= END; i++ ) {
                int idx = i - START;
                float dx = abs( pixel.x - ( center.x + float(i) ) );
                float dy = abs( pixel.y - ( center.y + float(i) ) );
                wx[idx] = ( dx < 1e-6 ) ? 1.0 : ( dx >= a ) ? 0.0 : a * sin( PI * dx ) * sin( PI * dx / a ) / ( PI * PI * dx * dx );
                wy[idx] = ( dy < 1e-6 ) ? 1.0 : ( dy >= a ) ? 0.0 : a * sin( PI * dy ) * sin( PI * dy / a ) / ( PI * PI * dy * dy );
            }

            vec3  col  = vec3( 0.0 );
            float wSum = 0.0;

            // anti-ringing accumulators
            vec3 lo = vec3( 1.0 );
            vec3 hi = vec3( 0.0 );

            for ( int j = 0; j < SIZE; j++ ) {
                for ( int i = 0; i < SIZE; i++ ) {
                    vec2  off   = vec2( float( i + START ), float( j + START ) );
                    ivec2 coord = clamp( ivec2( center + off ), ivec2( 0 ), imax );
                    vec3  s     = texelFetch( bitmapTexture, coord, 0 ).rgb;
                    float w     = wx[i] * wy[j];
                    col  += s * w;
                    wSum += w;

                    // track min/max of the 4 nearest texels inline
                    int ci = i + START;
                    int cj = j + START;
                    if ( ci >= 0 && ci <= 1 && cj >= 0 && cj <= 1 ) {
                        lo = min( lo, s );
                        hi = max( hi, s );
                    }
                }
            }
            col /= wSum;

            color.rgb = mix( col, clamp( col, lo, hi ), 0.5 );
        `,
    },
} as const satisfies ShaderModule<Props, Uniforms>;

export interface LanczosBitmapLayerProps {
    imageWidth:  number;
    imageHeight: number;
    weight?:     number;
}

export class LanczosBitmapLayer extends BitmapLayer<LanczosBitmapLayerProps> {
    static layerName = "LanczosBitmapLayer";
    static defaultProps = {
        ...BitmapLayer.defaultProps,
        imageWidth:  { type: "number", value: 512 },
        imageHeight: { type: "number", value: 512 },
        weight:      { type: "number", value: 3   },
    };

    getShaders() {
        const shaders = super.getShaders();
        const fs = shaders.fs.replace(
            /uniform\s+sampler2D\s+bitmapTexture\s*;/,
            "// bitmapTexture declared by lanczos module"
        );
        return { ...shaders, fs, modules: [...shaders.modules, lanczosModule] };
    }

    draw( opts: any ) {
        const { imageWidth, imageHeight, weight = 3 } = this.props;
        this.state.model?.shaderInputs.setProps({
            lanczos: { 
                weight:  Math.min( 3, weight ), 
                texSize: [imageWidth, imageHeight]
            },
        });
        super.draw( opts );
    }
}